import { DocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import {
  Divider,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';

import {
  MembershipNotifications_UpdateOrganization,
  MembershipNotifications_UpdateOrganizationVariables,
} from './graphql-types/MembershipNotifications_UpdateOrganization';
import { MembershipNotifications_User } from './graphql-types/MembershipNotifications_User';

const UPDATE_ORGANIZATION = gql`
  mutation MembershipNotifications_UpdateOrganization(
    $input: UpdateOrganizationV2Input!
  ) {
    updateOrganizationV2(input: $input) {
      ... on UpdateOrganizationV2Success {
        organization {
          _id
          optOutMembershipRenewalReminderEmails
        }
      }
    }
  }
`;

const GET_MEMBERSHIP_RENEWAL_REMINDER_EMAIL_CONFIG = gql`
  query MembershipNotifications_RenewalReminderEmailConfig {
    getmembershipRenewalReminderEmailConfig {
      settings {
        allowMembershipRenewalReminderEmails
      }
    }
  }
`;

export const MembershipNotifications: React.FC<{
  user: MembershipNotifications_User;
  onChange: (data: {
    optOutMembershipRenewalReminderEmails?: boolean;
  }) => Promise<void>;
  isAccountOwner: boolean;
  rosterOrganization: {
    _id: string;
    optOutMembershipRenewalReminderEmails: boolean | null;
  } | null;
}> & { userFragment: DocumentNode } = ({
  user,
  onChange,
  isAccountOwner,
  rosterOrganization,
}) => {
  const toast = useToast();
  const { data: membershipRenewalReminderEmailConfigData } = useQuery(
    GET_MEMBERSHIP_RENEWAL_REMINDER_EMAIL_CONFIG
  );

  const [updateOrganization] = useMutation<
    MembershipNotifications_UpdateOrganization,
    MembershipNotifications_UpdateOrganizationVariables
  >(UPDATE_ORGANIZATION, {
    refetchQueries: ['MyProfileNotificationsPage_User'],
    awaitRefetchQueries: true,
  });

  const onOrgChange = async (
    organizationId: string,
    optOutMembershipRenewalReminderEmails: boolean
  ): Promise<void> => {
    try {
      await updateOrganization({
        variables: {
          input: {
            id: organizationId,
            data: {
              optOutMembershipRenewalReminderEmails,
            },
          },
        },
      });
      toast({ status: 'success', description: 'Updated preference' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update preference.' });
      logError(err);
    }
  };

  const showOrgToggle = isAccountOwner && rosterOrganization != null;
  const showMembershipNotifications =
    membershipRenewalReminderEmailConfigData
      ?.getmembershipRenewalReminderEmailConfig?.settings
      ?.allowMembershipRenewalReminderEmails === true;

  if (!showMembershipNotifications) {
    return null;
  }

  return (
    <Card overflow="hidden">
      <Stack spacing="0">
        <Text
          fontSize="sm"
          p="4"
          bg="secondary.500"
          color="textPrimary.500"
          fontWeight="600"
        >
          Membership
        </Text>
        <Divider />
        <Stack spacing="0">
          <Stack px="4" py="4">
            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              w="full"
            >
              <FormLabel mb="0" color="textPrimary.500">
                Opt Out Membership Renewal Reminder Emails
              </FormLabel>
              <Switch
                isChecked={!!user.optOutMembershipRenewalReminderEmails}
                onChange={async ({ target: { checked } }) => {
                  await onChange({
                    optOutMembershipRenewalReminderEmails: checked,
                  });
                }}
              />
            </FormControl>
          </Stack>
          {showOrgToggle && rosterOrganization && (
            <>
              <Divider border="1px solid" borderColor="secondary.500" mt="0" />
              <Stack px="4" py="4">
                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  w="full"
                >
                  <FormLabel mb="0" color="textPrimary.500">
                    Opt Out Organization Renewal Reminder Emails
                  </FormLabel>
                  <Switch
                    isChecked={
                      !!rosterOrganization.optOutMembershipRenewalReminderEmails
                    }
                    onChange={async ({ target: { checked } }) => {
                      await onOrgChange(rosterOrganization._id, checked);
                    }}
                  />
                </FormControl>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

MembershipNotifications.userFragment = gql`
  fragment MembershipNotifications_User on User {
    _id
    optOutMembershipRenewalReminderEmails
  }
`;
